/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import { useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import { GET_RESOURCES_COLUMNS, RESOURCE_STATUSES } from '../constants';
import ConfirmResendingInvitation from '../../Assessments/List/widgets/ConfirmResendingInvitation';
import { ReactComponent as PendingIcon } from '../../../assets/icons/pending.svg';
import QhsTable from '../../Shared/Table/Table';
import MenuItem from '../../Shared/Table/widgets/MenuItem';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableActions from './widgetrs/TableActions';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const defaultConfirmResendInvitationState = {
  patientUUID: '',
  resource: '',
  accountUUID: '',
};

const ResourcesList = forwardRef(({
  setFilterStyle,
  setAggregations,
  setTotalCount,
  tableTitle,
  q,
  hideMenuActions,
  columnList,
}, ref) => {
  const { en: { titles } } = useSelector((state) => state.i18n);
  const { accountUuid: paramAccountUuid } = useParams();
  const {
    isDistributionSponsor,
    metadata: { currentAccount },
  } = useSelector((state) => state.user);
  const [modifiedItems, setModifiedItems] = useState();
  const [openConfirmResentDialog, toggleConfirmResentDialog] = useState(defaultConfirmResendInvitationState);
  const tableKey = isDistributionSponsor ? TABLE_KEYS.resourcesDSTable : TABLE_KEYS.resourcesTable;
  const columns = useGenerateColumns(columnList || GET_RESOURCES_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const handleConfirmResentDialog = (
    e,
    { patientUUID, uuid, patientEmail, patientPhone, accountUUID },
  ) => {
    toggleConfirmResentDialog({
      sendInviteToPatientEmail: patientEmail,
      sendInviteToPatientPhone: patientPhone,
      patientUUID,
      resource: uuid,
      accountUUID,
    });
  };

  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid}/resources`;

  const {
    items,
    totalCount,
    aggregations,
    reloadData,
    clearFilter,
    loading: dataLoading,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    tableKey,
    canLoad: fields.length,
  });

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    setAggregations && setAggregations(aggregations);
  }, [aggregations]);

  useEffect(() => {
    setTotalCount && setTotalCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter);
      setModifiedItems(newModifiedItems);
    }
  }, [items]);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle(!!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0));
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const reloadResourceList = (hasFilter) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadResourceList(v);
    },
  }));

  const checkIsResendingDisabled = (resourceUuid) => {
    if (dataLoading || !resourceUuid) {
      return true;
    }
    let isDisabled = false;

    modifiedItems.forEach((resource) => {
      if (resource.uuid === resourceUuid) {
        isDisabled =
          resource.status.toLowerCase() ===
          RESOURCE_STATUSES.scheduled.toLowerCase();
      }
    });
    return isDisabled;
  };

  return (
    <>
      {openConfirmResentDialog.resource && (
        <ConfirmResendingInvitation
          source='resource'
          handleClose={() =>
            toggleConfirmResentDialog(defaultConfirmResendInvitationState)
          }
          reloadData={reloadData}
          resendInvitationData={openConfirmResentDialog}
        />
      )}
      <QhsTable
        columns={columns}
        data={modifiedItems || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !modifiedItems}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        tableTitle={tableTitle}
        showRefreshTableButton
        reloadData={reloadResourceList}
        menuActions={!hideMenuActions && <TableActions
          totalCount={totalCount}
          tableSort={sort}
          tableFilter={filter}
          columns={columns}
          aggregations={aggregations}
        />}
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <>
            <MenuItem
              title={titles.resendInvitation}
              icon={<PendingIcon />}
              onClick={(e) => handleConfirmResentDialog(e, row.original)}
              setAnchorEl={setAnchorEl}
              isDisabled={checkIsResendingDisabled}
              rowUuid={row.original.uuid}
            />
          </>
        )}
      />
    </>
  );
});

export default ResourcesList;
